import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon } from 'lucide-react';
import {
  uploadHomepageBanner,
  fetchHomepageBanners,
  updateHomepageBanner,
  deleteHomepageBanner,
} from '../services/homepageBannerService';

const UploadImages = () => {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState('');
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const loadBanners = async () => {
    const data = await fetchHomepageBanners();
    if (data) setBanners(data);
  };

  useEffect(() => {
    loadBanners();
  }, []);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;
    setFile(selectedFile);
    setPreview(URL.createObjectURL(selectedFile));
  };

  const handleUpload = async () => {
    if (!file) return;
    setLoading(true);

    const formData = new FormData();
    formData.append('image', file);

    const result = await uploadHomepageBanner(formData);
    if (result) {
      setFile(null);
      setPreview('');
      loadBanners();
    }
    setLoading(false);
  };

  const toggleStatus = async (id, isActive) => {
    await updateHomepageBanner(id, { isActive: !isActive });
    loadBanners();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this image?')) return;
    await deleteHomepageBanner(id);
    loadBanners();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 py-6 sm:py-8">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <button
            onClick={() => navigate(-1)}
            className="
              inline-flex
              items-center
              gap-2
              px-3
              py-2
              rounded-md
              bg-gray-200
              text-gray-700
              text-sm
              font-medium
              hover:bg-gray-300
              transition
            "
          >
            <ArrowLeftIcon size={16} />
            Back
          </button>

          <h1 className="text-xl sm:text-2xl font-semibold text-gray-800">
            Homepage Images
          </h1>
        </div>

        {/* Upload Section */}
        <div className="bg-white rounded-xl shadow p-4 sm:p-6 mb-10">
          <div className="flex flex-col gap-4">
            <input
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              className="
                block
                w-full
                text-sm
                border
                border-gray-300
                rounded-md
                file:mr-4
                file:py-2
                file:px-4
                file:rounded-md
                file:border-0
                file:bg-blue-50
                file:text-blue-700
                hover:file:bg-blue-100
              "
            />

            {preview && (
              <div className="bg-gray-50 rounded-lg p-3 flex justify-center">
                <img
                  src={preview}
                  alt="Preview"
                  className="max-h-72 object-contain"
                />
              </div>
            )}

            <button
              onClick={handleUpload}
              disabled={loading}
              className="
                w-full
                sm:w-fit
                bg-blue-600
                hover:bg-blue-700
                text-white
                px-6
                py-2
                rounded-md
                text-sm
                font-medium
                transition
                disabled:opacity-50
              "
            >
              {loading ? 'Uploading...' : 'Upload Image'}
            </button>
          </div>
        </div>

        {/* Banner List */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {banners.map((banner) => (
            <div
              key={banner._id}
              className="bg-white rounded-xl shadow overflow-hidden"
            >
              <div className="h-44 sm:h-48 bg-gray-50 flex items-center justify-center">
                <img
                  src={banner.imageUrl}
                  alt="Banner"
                  className="max-h-full max-w-full object-contain"
                />
              </div>

              <div className="p-4 flex justify-between items-center">
                <button
                  onClick={() => toggleStatus(banner._id, banner.isActive)}
                  className={`px-3 py-1 rounded-full text-xs font-medium ${banner.isActive
                      ? 'bg-green-100 text-green-700'
                      : 'bg-gray-200 text-gray-600'
                    }`}
                >
                  {banner.isActive ? 'Active' : 'Inactive'}
                </button>

                <button
                  onClick={() => handleDelete(banner._id)}
                  className="text-red-600 hover:text-red-800 text-sm font-medium"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default UploadImages;
